// TRATAMIENTO DE LISTAS (como «Tratamiento de Listas de Precio y Costo» de Random; pedido de Rodrigo,
// 2026-10-13). Una lista a la vez, todos sus productos en una grilla: precio por UNIDAD y por CAJA
// (Ud1/Ud2 de Random) y sus ESCALAS (la f(x) de Random, legible: «1–23: $1.495 · 24+: $1.375»).
// Se corrige en la celda (Enter baja a la fila siguiente), se ajusta un % a lo filtrado y se guarda
// todo junto en UN comando auditado. Un precio no se pisa: nace otro con vigencia desde hoy, salvo
// que el vigente sea de hoy mismo (corrección del día).
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/cliente';
import { useIntentos } from '../api/idempotencia';
import { PanelError } from '../comun/Estados';
import { pesos } from '../comun/formato';
import { Modal } from '../comun/Modal';
import { useSesion } from '../sesion/SesionContexto';
import {
  aApi,
  aEdicion,
  ajustar,
  homologar,
  caminoDe,
  hijasDe,
  nombreNivel,
  textoEscalas,
  useArbolCategorias,
  useListasDeEmpresa,
  useMarcasFila,
  useTratamiento,
  valorDigitado,
  type FilaTratamiento,
  type PresentacionConPrecio,
  type TramoApi,
  type TramoEdicion,
} from './datos';
import { coincide } from '../comun/coincide';

/** Un cambio pendiente de una presentación: su valor y, si se tocaron, sus escalas. */
interface Cambio {
  valor: string;
  escalas: TramoEdicion[] | null;
}
type Columna = 'u' | 'c';
const clave = (productoId: string, presentacionId: string) => `${productoId}|${presentacionId}`;
const POR_PAGINA = 300;

/** La presentación base (unidad) y la caja (la vendible más grande que no es la base). */
export function unidadYCaja(f: Pick<FilaTratamiento, 'presentaciones'>) {
  const unidad = f.presentaciones.find((x) => x.esBase) ?? f.presentaciones[0] ?? null;
  const caja =
    [...f.presentaciones]
      .filter((x) => x !== unidad)
      .sort((a, b) => Number(b.factor) - Number(a.factor))[0] ?? null;
  return { unidad, caja };
}

export function TratamientoListas({
  listaInicial,
  listaFija = false,
  alAbrirProducto,
}: {
  listaInicial?: string;
  /** Dentro del mantenedor de listas: la lista ya viene elegida. */
  listaFija?: boolean;
  alAbrirProducto?: (p: { id: string; sku: string; nombre: string }) => void;
}) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  const intentos = useIntentos();
  const listas = useListasDeEmpresa(empresaId);
  const categorias = useArbolCategorias();
  const marcas = useMarcasFila();
  const [listaId, setListaId] = useState<string | null>(listaInicial ?? null);
  useEffect(() => {
    if (listaId || !listas.data?.length) return;
    const venta =
      listas.data.find((l) => l.codigo === '01P') ?? listas.data.find((l) => l.uso === 'venta');
    setListaId((venta ?? listas.data[0]!).id);
  }, [listaId, listas.data]);
  const lista = listas.data?.find((l) => l.id === listaId) ?? null;

  // Filtros: súper familia › familia › subfamilia en cascada, marca (servidor) y texto (local).
  const [sf, setSf] = useState('');
  const [fa, setFa] = useState('');
  const [sub, setSub] = useState('');
  const [marcaId, setMarcaId] = useState('');
  const [texto, setTexto] = useState('');
  const [soloSinPrecio, setSoloSinPrecio] = useState(false);
  const [soloCambios, setSoloCambios] = useState(false);
  // Unidad y caja van juntas por omisión: cambiar una fija la otra por el factor.
  const [ligados, setLigados] = useState(true);
  const categoriaId = sub || fa || sf;
  const q = useTratamiento(empresaId, listaId, {
    ...(categoriaId ? { categoriaId } : {}),
    ...(marcaId ? { marcaId } : {}),
  });
  const cats = categorias.data ?? [];
  const porId = useMemo(() => new Map(cats.map((c) => [c.id, c])), [cats]);
  const nombreMarca = useMemo(
    () => new Map((marcas.data ?? []).map((m) => [m.id, m.nombre])),
    [marcas.data],
  );

  const [cambios, setCambios] = useState<Map<string, Cambio>>(new Map());
  useEffect(() => setCambios(new Map()), [listaId]);
  const filas = useMemo(() => {
    const t = texto.trim().toLowerCase();
    return (q.data ?? []).filter((f) => {
      if (t && !coincide(t, f.sku, f.nombre)) return false;
      const { unidad, caja } = unidadYCaja(f);
      if (soloSinPrecio && unidad?.precio && (!caja || caja.precio)) return false;
      if (
        soloCambios &&
        !f.presentaciones.some((x) => cambios.has(clave(f.productoId, x.presentacionId)))
      )
        return false;
      return true;
    });
  }, [q.data, texto, soloSinPrecio, soloCambios, cambios]);
  const [visibles, setVisibles] = useState(POR_PAGINA);
  useEffect(() => setVisibles(POR_PAGINA), [listaId, categoriaId, marcaId, texto, soloSinPrecio]);

  const edita = puede('precios.precios.gestionar') && !!lista;
  const [editando, setEditando] = useState<{ fila: number; col: Columna } | null>(null);
  const [escalasDe, setEscalasDe] = useState<FilaTratamiento | null>(null);
  const [pct, setPct] = useState('');
  const [motivo, setMotivo] = useState('');

  const valorActual = (f: FilaTratamiento, p: PresentacionConPrecio | null) =>
    p
      ? (cambios.get(clave(f.productoId, p.presentacionId))?.valor ?? p.precio?.valor ?? null)
      : null;
  const escalasActuales = (f: FilaTratamiento, p: PresentacionConPrecio): TramoEdicion[] =>
    cambios.get(clave(f.productoId, p.presentacionId))?.escalas ??
    aEdicion(p.precio?.escalas ?? []);

  const anotar = (f: FilaTratamiento, p: PresentacionConPrecio, c: Partial<Cambio>) =>
    setCambios((m) => {
      const n = new Map(m);
      const k = clave(f.productoId, p.presentacionId);
      const previo = n.get(k);
      const valor = c.valor ?? previo?.valor ?? (p.precio ? String(Number(p.precio.valor)) : '0');
      const escalas = c.escalas !== undefined ? c.escalas : (previo?.escalas ?? null);
      // Volver al valor guardado sin tocar escalas = no hay cambio.
      if (escalas === null && p.precio && Number(valor) === Number(p.precio.valor)) n.delete(k);
      else n.set(k, { valor, escalas });
      return n;
    });

  const aplicarPorcentaje = () => {
    const n = Number(pct.replace(',', '.'));
    if (!Number.isFinite(n) || n === 0) return;
    for (const f of filas)
      for (const p of [unidadYCaja(f).unidad, unidadYCaja(f).caja]) {
        if (!p) continue;
        const base = valorActual(f, p);
        if (base === null) continue;
        const tramos = escalasActuales(f, p);
        anotar(f, p, {
          valor: ajustar(base, n),
          escalas: tramos.length ? tramos.map((t) => ({ ...t, valor: ajustar(t.valor, n) })) : null,
        });
      }
    setPct('');
  };

  const guardar = useMutation({
    mutationFn: () => {
      const datos = q.data ?? [];
      const porProducto = new Map(datos.map((f) => [f.productoId, f]));
      const precios = [...cambios.entries()].map(([k, c]) => {
        const [productoId, presentacionId] = k.split('|') as [string, string];
        const p = porProducto
          .get(productoId)
          ?.presentaciones.find((x) => x.presentacionId === presentacionId);
        // Si solo cambió el valor, las escalas vigentes se conservan.
        const escalas = c.escalas ?? aEdicion(p?.precio?.escalas ?? []);
        return { listaId, productoId, presentacionId, valor: c.valor, escalas: aApi(escalas) };
      });
      const cuerpo = {
        empresaId,
        motivo: motivo.trim().length >= 3 ? motivo.trim() : `Tratamiento de lista ${lista?.codigo}`,
        precios,
      };
      return api.post('/precios/lote', cuerpo, {
        idempotencia: intentos.clave('precios-lote', cuerpo),
      });
    },
    onSuccess: async () => {
      intentos.cerrar();
      setCambios(new Map());
      setMotivo('');
      await qc.invalidateQueries({ queryKey: ['precios'] });
    },
    onError: (e) => intentos.cerrar(e),
  });

  const exportar = () => {
    const esc = (x: string) => (/[;"\n]/.test(x) ? `"${x.replace(/"/g, '""')}"` : x);
    const n = (x: string | null) => (x === null ? '' : String(Number(x)).replace('.', ','));
    const lineas = [
      [
        'SKU',
        'Producto',
        'Marca',
        'Súper familia',
        'Familia',
        'Subfamilia',
        'Precio unidad',
        'Precio caja',
        'Unidades por caja',
        'Escalas unidad',
      ].join(';'),
      ...filas.map((f) => {
        const { unidad, caja } = unidadYCaja(f);
        const camino = caminoDe(f.categoriaId, porId);
        return [
          esc(f.sku),
          esc(f.nombre),
          esc(f.marcaId ? (nombreMarca.get(f.marcaId) ?? '') : ''),
          esc(camino[0]?.nombre ?? ''),
          esc(camino[1]?.nombre ?? ''),
          esc(camino[2]?.nombre ?? ''),
          n(valorActual(f, unidad)),
          n(valorActual(f, caja)),
          caja ? n(caja.factor) : '',
          esc(unidad ? textoEscalas(aApi(escalasActuales(f, unidad)).map(deApi)) : ''),
        ].join(';');
      }),
    ];
    const b = new Blob([`${String.fromCharCode(0xfeff)}${lineas.join('\r\n')}`], {
      type: 'text/csv;charset=utf-8',
    });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(b);
    a.download = `lista-${lista?.codigo ?? 'precios'}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const opciones = (padre: string | null) =>
    hijasDe(cats, padre).map((c) => (
      <option key={c.id} value={c.id}>
        {c.nombre}
      </option>
    ));

  return (
    <div className="pantalla tratamiento-listas" data-pantalla="tratamiento-listas">
      <div className="barra-filtros">
        {!listaFija && (
          <label className="campo">
            <span className="etiqueta">Lista</span>
            <select
              aria-label="Lista de precios"
              value={listaId ?? ''}
              onChange={(e) => {
                if (
                  cambios.size &&
                  !window.confirm('Hay cambios sin guardar. ¿Cambiar de lista y perderlos?')
                )
                  return;
                setListaId(e.target.value);
              }}
            >
              {(listas.data ?? []).map((l) => (
                <option key={l.id} value={l.id}>
                  {l.codigo} · {l.nombre}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="campo">
          <span className="etiqueta">{nombreNivel(1)}</span>
          <select
            aria-label={nombreNivel(1)}
            value={sf}
            onChange={(e) => {
              setSf(e.target.value);
              setFa('');
              setSub('');
            }}
          >
            <option value="">Todas</option>
            {opciones(null)}
          </select>
        </label>
        <label className="campo">
          <span className="etiqueta">{nombreNivel(2)}</span>
          <select
            aria-label={nombreNivel(2)}
            value={fa}
            disabled={!sf}
            onChange={(e) => {
              setFa(e.target.value);
              setSub('');
            }}
          >
            <option value="">Todas</option>
            {sf && opciones(sf)}
          </select>
        </label>
        <label className="campo">
          <span className="etiqueta">{nombreNivel(3)}</span>
          <select
            aria-label={nombreNivel(3)}
            value={sub}
            disabled={!fa}
            onChange={(e) => setSub(e.target.value)}
          >
            <option value="">Todas</option>
            {fa && opciones(fa)}
          </select>
        </label>
        <label className="campo">
          <span className="etiqueta">Marca</span>
          <select aria-label="Marca" value={marcaId} onChange={(e) => setMarcaId(e.target.value)}>
            <option value="">Todas</option>
            {(marcas.data ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.nombre}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          <span className="etiqueta">Buscar</span>
          <input
            aria-label="Buscar en la lista"
            placeholder="SKU o nombre…"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
        </label>
        <label className="casilla-filtro">
          <input
            type="checkbox"
            checked={soloSinPrecio}
            onChange={(e) => setSoloSinPrecio(e.target.checked)}
          />{' '}
          Sin precio
        </label>
        {edita && (
          <label
            className="casilla-filtro"
            title="Al cambiar el precio unidad, la caja se fija por el factor (unidad × factor) y al revés (caja ÷ factor)"
          >
            <input
              type="checkbox"
              checked={ligados}
              onChange={(e) => setLigados(e.target.checked)}
            />{' '}
            Homologar unidad ↔ caja
          </label>
        )}
        {cambios.size > 0 && (
          <label className="casilla-filtro">
            <input
              type="checkbox"
              checked={soloCambios}
              onChange={(e) => setSoloCambios(e.target.checked)}
            />{' '}
            Solo cambiados
          </label>
        )}
        <span className="espaciador" />
        <button type="button" onClick={exportar} disabled={!filas.length}>
          Excel
        </button>
      </div>
      {edita && (
        <div className="barra-filtros barra-tratamiento">
          <label className="campo">
            <span className="etiqueta">Ajustar % a lo filtrado</span>
            <span className="fila-ajuste">
              <input
                aria-label="Porcentaje de ajuste"
                placeholder="ej. 5 o -3"
                value={pct}
                onChange={(e) => setPct(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && aplicarPorcentaje()}
              />
              <button
                type="button"
                disabled={!pct.trim() || !filas.length}
                onClick={aplicarPorcentaje}
              >
                Aplicar a {filas.length} productos
              </button>
            </span>
          </label>
          <span className="espaciador" />
          {cambios.size > 0 && (
            <>
              <label className="campo">
                <span className="etiqueta">Motivo (opcional)</span>
                <input
                  aria-label="Motivo del cambio"
                  placeholder={`Tratamiento de lista ${lista?.codigo ?? ''}`}
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                />
              </label>
              <button
                type="button"
                onClick={() => setCambios(new Map())}
                disabled={guardar.isPending}
              >
                Deshacer cambios
              </button>
              <button
                type="button"
                className="primario"
                disabled={guardar.isPending}
                onClick={() => guardar.mutate()}
              >
                {guardar.isPending
                  ? 'Guardando…'
                  : `Guardar ${cambios.size} precio${cambios.size === 1 ? '' : 's'}`}
              </button>
            </>
          )}
        </div>
      )}
      {guardar.isError && <PanelError error={guardar.error} />}
      {q.isError && <PanelError error={q.error} />}
      <p className="apagado resumen-tratamiento">
        {lista
          ? `${lista.codigo} · ${lista.nombre} · precios ${lista.semantica === 'bruto' ? 'con IVA' : 'netos'}`
          : ''}
        {q.data ? ` · ${filas.length} de ${q.data.length} productos` : ''}
        {edita ? ' · clic en un precio para cambiarlo, Enter baja a la fila siguiente' : ''}
      </p>
      <div className="grilla-scroll">
        <table className="grilla compacta grilla-tratamiento" aria-label="Tratamiento de lista">
          <thead>
            <tr>
              <th>SKU</th>
              <th>Producto</th>
              <th>Marca</th>
              <th>Familia › Subfamilia</th>
              <th className="der">Precio unidad</th>
              <th className="der">Precio caja</th>
              <th className="der">Caja ÷ un.</th>
              <th>Escalas (f(x))</th>
            </tr>
          </thead>
          <tbody>
            {filas.slice(0, visibles).map((f, i) => {
              const { unidad, caja } = unidadYCaja(f);
              const camino = caminoDe(f.categoriaId, porId);
              const vu = valorActual(f, unidad);
              const vc = valorActual(f, caja);
              const tramosU = unidad ? escalasActuales(f, unidad) : [];
              const tramosC = caja ? escalasActuales(f, caja) : [];
              const escCambiada =
                (unidad && cambios.get(clave(f.productoId, unidad.presentacionId))?.escalas) ||
                (caja && cambios.get(clave(f.productoId, caja.presentacionId))?.escalas);
              return (
                <tr key={f.productoId} className={f.estado !== 'activo' ? 'apagado' : undefined}>
                  <td className="mono">
                    {alAbrirProducto ? (
                      <button
                        type="button"
                        className="enlace"
                        title="Abrir la ficha del producto"
                        onClick={() =>
                          alAbrirProducto({ id: f.productoId, sku: f.sku, nombre: f.nombre })
                        }
                      >
                        {f.sku}
                      </button>
                    ) : (
                      f.sku
                    )}
                  </td>
                  <td>{f.nombre}</td>
                  <td>{f.marcaId ? (nombreMarca.get(f.marcaId) ?? '') : ''}</td>
                  <td className="apagado" title={camino.map((c) => c.nombre).join(' › ')}>
                    {camino
                      .slice(1)
                      .map((c) => c.nombre)
                      .join(' › ')}
                  </td>
                  {(['u', 'c'] as const).map((col) => {
                    const p = col === 'u' ? unidad : caja;
                    const v = col === 'u' ? vu : vc;
                    if (!p)
                      return (
                        <td key={col} className="der apagado">
                          —
                        </td>
                      );
                    const k = clave(f.productoId, p.presentacionId);
                    return (
                      <CeldaPrecio
                        key={col}
                        etiqueta={`${col === 'u' ? 'Precio unidad' : 'Precio caja'} ${f.sku}`}
                        valor={v}
                        sufijo={col === 'c' ? `${p.codigo} ×${Number(p.factor)}` : undefined}
                        cambiado={cambios.has(k)}
                        editable={edita}
                        editando={editando?.fila === i && editando.col === col}
                        alEditar={() => setEditando({ fila: i, col })}
                        alSalir={(nuevo, siguiente) => {
                          if (nuevo !== null) {
                            const otro = col === 'u' ? caja : unidad;
                            const antes = valorActual(f, p);
                            anotar(f, p, { valor: nuevo });
                            if (ligados && otro && Number(nuevo) !== Number(antes ?? -1))
                              anotar(f, otro, {
                                valor: homologar(nuevo, p.factor, otro.factor),
                              });
                          }
                          setEditando(
                            siguiente && i + 1 < Math.min(filas.length, visibles)
                              ? { fila: i + 1, col }
                              : null,
                          );
                        }}
                      />
                    );
                  })}
                  <td className="der apagado">
                    {caja && vc !== null
                      ? pesos(String(Math.round(Number(vc) / Number(caja.factor))))
                      : ''}
                  </td>
                  <td className={escCambiada ? 'celda-cambiada' : undefined}>
                    {edita && unidad ? (
                      <button
                        type="button"
                        className="enlace escalas-celda"
                        aria-label={`Escalas de ${f.sku}`}
                        onClick={() => setEscalasDe(f)}
                      >
                        {resumenEscalas(tramosU, tramosC, caja?.codigo) || (
                          <span className="apagado">+ escalas</span>
                        )}
                      </button>
                    ) : (
                      resumenEscalas(tramosU, tramosC, caja?.codigo)
                    )}
                  </td>
                </tr>
              );
            })}
            {!filas.length && (
              <tr>
                <td colSpan={8} className="grilla-vacia">
                  {q.isLoading || !listaId ? 'Cargando…' : 'Sin productos con esos filtros'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {filas.length > visibles && (
        <button
          type="button"
          className="mas-filas"
          onClick={() => setVisibles((v) => v + POR_PAGINA)}
        >
          Mostrar {Math.min(POR_PAGINA, filas.length - visibles)} más ({filas.length - visibles}{' '}
          restantes)
        </button>
      )}
      {escalasDe && (
        <EditorEscalas
          fila={escalasDe}
          tramos={(p) => escalasActuales(escalasDe, p)}
          valor={(p) => valorActual(escalasDe, p)}
          alGuardar={(p, tramos) => anotar(escalasDe, p, { escalas: tramos })}
          alCerrar={() => setEscalasDe(null)}
        />
      )}
    </div>
  );
}

const deApi = (x: { cantidadDesde: string; cantidadHasta?: string; valor: string }): TramoApi => ({
  desde: x.cantidadDesde,
  hasta: x.cantidadHasta ?? null,
  valor: x.valor,
});

function resumenEscalas(u: TramoEdicion[], c: TramoEdicion[], codigoCaja?: string) {
  const partes = [];
  if (u.length) partes.push(textoEscalas(aApi(u).map(deApi)));
  if (c.length) partes.push(`${codigoCaja ?? 'caja'}: ${textoEscalas(aApi(c).map(deApi))}`);
  return partes.join(' | ');
}

/** Celda de precio: muestra el valor; con clic se edita. Enter guarda y baja; Esc cancela. */
function CeldaPrecio({
  etiqueta,
  valor,
  sufijo,
  cambiado,
  editable,
  editando,
  alEditar,
  alSalir,
}: {
  etiqueta: string;
  valor: string | null;
  sufijo?: string | undefined;
  cambiado: boolean;
  editable: boolean;
  editando: boolean;
  alEditar: () => void;
  alSalir: (nuevo: string | null, siguiente: boolean) => void;
}) {
  const [texto, setTexto] = useState('');
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!editando) return;
    setTexto(valor === null ? '' : String(Number(valor)));
    requestAnimationFrame(() => ref.current?.select());
  }, [editando, valor]);
  const clase = `der celda-precio${cambiado ? ' celda-cambiada' : ''}`;
  if (editando)
    return (
      <td className={clase}>
        <input
          ref={ref}
          aria-label={etiqueta}
          className="entrada-precio"
          inputMode="decimal"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onBlur={() => alSalir(valorDigitado(texto), false)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              alSalir(valorDigitado(texto), true);
            } else if (e.key === 'Escape') {
              e.preventDefault();
              alSalir(null, false);
            }
          }}
        />
      </td>
    );
  return (
    <td className={clase}>
      {editable ? (
        <button type="button" className="valor-editable" aria-label={etiqueta} onClick={alEditar}>
          {valor === null ? <span className="apagado">sin precio</span> : pesos(valor)}
        </button>
      ) : valor === null ? (
        <span className="apagado">sin precio</span>
      ) : (
        pesos(valor)
      )}
      {sufijo && <span className="apagado sufijo-caja"> {sufijo}</span>}
    </td>
  );
}

/**
 * Las escalas de un producto (la f(x) de Random) como TRAMOS: desde qué cantidad rige qué
 * precio. El último queda abierto («y más»). Sin escribir la fórmula, sin errores de sintaxis.
 */
function EditorEscalas({
  fila,
  tramos,
  valor,
  alGuardar,
  alCerrar,
}: {
  fila: FilaTratamiento;
  tramos: (p: PresentacionConPrecio) => TramoEdicion[];
  valor: (p: PresentacionConPrecio) => string | null;
  alGuardar: (p: PresentacionConPrecio, t: TramoEdicion[]) => void;
  alCerrar: () => void;
}) {
  const { unidad, caja } = unidadYCaja(fila);
  const opciones = [unidad, caja].filter((x): x is PresentacionConPrecio => !!x);
  const [pres, setPres] = useState(opciones[0]!);
  const [filas, setFilas] = useState<TramoEdicion[]>(() => tramos(opciones[0]!));
  const cambiarPres = (p: PresentacionConPrecio) => {
    setPres(p);
    setFilas(tramos(p));
  };
  const validas = filas.every(
    (t) => valorDigitado(t.desde) !== null && valorDigitado(t.valor) !== null,
  );
  const desdes = filas.map((t) => Number(valorDigitado(t.desde)));
  const repetidas = new Set(desdes).size !== desdes.length;
  return (
    <Modal titulo={`Escalas · ${fila.sku} ${fila.nombre}`} alCerrar={alCerrar}>
      {opciones.length > 1 && (
        <div className="segmentos" role="tablist">
          {opciones.map((p) => (
            <button
              key={p.presentacionId}
              type="button"
              className={p === pres ? 'activo' : undefined}
              onClick={() => cambiarPres(p)}
            >
              {p.esBase ? 'Por unidad' : `Por ${p.codigo} ×${Number(p.factor)}`}
            </button>
          ))}
        </div>
      )}
      <p className="apagado">
        Precio base {valor(pres) === null ? 'sin precio' : pesos(valor(pres))}. Cada tramo rige
        desde esa cantidad (en {pres.esBase ? 'unidades' : `${pres.codigo}`}) hasta la siguiente; el
        último, «y más». Sin tramos, el precio es plano.
      </p>
      <table className="grilla compacta" aria-label="Tramos">
        <thead>
          <tr>
            <th className="der">Desde cantidad</th>
            <th className="der">Precio</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {filas.map((t, i) => (
            <tr key={i}>
              <td className="der">
                <input
                  aria-label={`Tramo ${i + 1} desde`}
                  className="entrada-precio"
                  value={t.desde}
                  onChange={(e) =>
                    setFilas((s) =>
                      s.map((x, j) => (j === i ? { ...x, desde: e.target.value } : x)),
                    )
                  }
                />
              </td>
              <td className="der">
                <input
                  aria-label={`Tramo ${i + 1} precio`}
                  className="entrada-precio"
                  value={t.valor}
                  onChange={(e) =>
                    setFilas((s) =>
                      s.map((x, j) => (j === i ? { ...x, valor: e.target.value } : x)),
                    )
                  }
                />
              </td>
              <td>
                <button
                  type="button"
                  className="boton-mini"
                  aria-label={`Quitar tramo ${i + 1}`}
                  onClick={() => setFilas((s) => s.filter((_, j) => j !== i))}
                >
                  ✕
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button
        type="button"
        onClick={() =>
          setFilas((s) => [
            ...s,
            {
              desde: s.length ? '' : '0',
              valor: valor(pres) === null ? '' : String(Number(valor(pres))),
            },
          ])
        }
      >
        Agregar tramo
      </button>
      {repetidas && <p className="tono-aviso">Dos tramos empiezan en la misma cantidad.</p>}
      {filas.length > 0 && (
        <p className="apagado">
          Queda:{' '}
          {textoEscalas(
            aApi(
              filas
                .filter((t) => valorDigitado(t.desde) && valorDigitado(t.valor))
                .map((t) => ({ desde: valorDigitado(t.desde)!, valor: valorDigitado(t.valor)! })),
            ).map(deApi),
          )}
        </p>
      )}
      <div className="acciones-modal">
        <button type="button" onClick={alCerrar}>
          Volver
        </button>
        <button
          type="button"
          className="primario"
          disabled={!validas || repetidas}
          onClick={() => {
            alGuardar(
              pres,
              filas.map((t) => ({
                desde: valorDigitado(t.desde)!,
                valor: valorDigitado(t.valor)!,
              })),
            );
            alCerrar();
          }}
        >
          Dejar escalas
        </button>
      </div>
    </Modal>
  );
}
